/**
 * Enterprise Architecture Design Framework
 * Well-Architected Framework + Layered Architecture + Distributed Design Patterns
 * Supports HighJump WMS transformation and enterprise database design
 */
// Well-Architected Framework Pillars
export const wellArchitectedPillars = [
    {
        id: 'reliability',
        name: 'Reliability',
        description: 'Ability to recover from failures and meet demand',
        principles: [
            'Design for failure',
            'Implement redundancy',
            'Monitor health and performance',
            'Automate recovery processes',
            'Test disaster recovery procedures'
        ],
        designPatterns: [
            'Circuit Breaker',
            'Retry Pattern',
            'Bulkhead',
            'Health Check',
            'Graceful Degradation'
        ],
        azureServices: [
            'Azure Site Recovery',
            'Azure Backup',
            'Azure Traffic Manager',
            'Azure Load Balancer',
            'Azure Application Gateway'
        ],
        keyMetrics: [
            'Recovery Time Objective (RTO)',
            'Recovery Point Objective (RPO)',
            'Mean Time to Recovery (MTTR)',
            'Availability percentage',
            'Error rate'
        ],
        commonAntiPatterns: [
            'Single point of failure',
            'Cascading failures',
            'Assuming infrastructure never fails',
            'No disaster recovery testing',
            'Ignoring dependencies'
        ]
    },
    {
        id: 'security',
        name: 'Security',
        description: 'Protect against threats and vulnerabilities',
        principles: [
            'Defense in depth',
            'Zero trust',
            'Least privilege access',
            'Encrypt data at rest and in transit',
            'Monitor and audit access'
        ],
        designPatterns: [
            'Identity and Access Management',
            'API Gateway',
            'Secure Communication',
            'Data Encryption',
            'Security Monitoring'
        ],
        azureServices: [
            'Azure Active Directory',
            'Azure Key Vault',
            'Azure Security Center',
            'Azure Sentinel',
            'Azure API Management'
        ],
        keyMetrics: [
            'Security incidents',
            'Vulnerability scan results',
            'Compliance score',
            'Access review completion',
            'Encryption coverage'
        ],
        commonAntiPatterns: [
            'Hardcoded secrets',
            'Overprivileged access',
            'No encryption',
            'Security as afterthought',
            'No monitoring'
        ]
    },
    {
        id: 'cost-optimization',
        name: 'Cost Optimization',
        description: 'Maximize value and minimize waste',
        principles: [
            'Pay for what you use',
            'Right-size resources',
            'Use reserved capacity',
            'Monitor and analyze costs',
            'Optimize continuously'
        ],
        designPatterns: [
            'Auto-scaling',
            'Resource tagging',
            'Cost allocation',
            'Reserved instances',
            'Spot instances'
        ],
        azureServices: [
            'Azure Cost Management',
            'Azure Advisor',
            'Azure Reservations',
            'Azure Spot VMs',
            'Azure Hybrid Benefit'
        ],
        keyMetrics: [
            'Cost per transaction',
            'Resource utilization',
            'Reserved capacity usage',
            'Cost trends',
            'Budget variance'
        ],
        commonAntiPatterns: [
            'Over-provisioning',
            'No cost monitoring',
            'Ignoring recommendations',
            'No resource tagging',
            'Manual scaling'
        ]
    },
    {
        id: 'operational-excellence',
        name: 'Operational Excellence',
        description: 'Run and monitor systems effectively',
        principles: [
            'Automate operations',
            'Monitor everything',
            'Learn from failures',
            'Implement IaC',
            'Practice DevOps'
        ],
        designPatterns: [
            'Infrastructure as Code',
            'CI/CD Pipeline',
            'Monitoring and Alerting',
            'Logging and Tracing',
            'Configuration Management'
        ],
        azureServices: [
            'Azure DevOps',
            'Azure Monitor',
            'Azure Log Analytics',
            'Azure Automation',
            'Azure Resource Manager'
        ],
        keyMetrics: [
            'Deployment frequency',
            'Lead time for changes',
            'Mean time to recovery',
            'Change failure rate',
            'System availability'
        ],
        commonAntiPatterns: [
            'Manual processes',
            'No monitoring',
            'Poor documentation',
            'No automation',
            'Siloed teams'
        ]
    },
    {
        id: 'performance-efficiency',
        name: 'Performance Efficiency',
        description: 'Use computing resources efficiently',
        principles: [
            'Right-size for workload',
            'Scale horizontally',
            'Use caching strategically',
            'Optimize data access',
            'Monitor performance'
        ],
        designPatterns: [
            'Caching',
            'Load Balancing',
            'Database Optimization',
            'CDN',
            'Asynchronous Processing'
        ],
        azureServices: [
            'Azure CDN',
            'Azure Redis Cache',
            'Azure SQL Database',
            'Azure Functions',
            'Azure Service Bus'
        ],
        keyMetrics: [
            'Response time',
            'Throughput',
            'Resource utilization',
            'Cache hit ratio',
            'Queue length'
        ],
        commonAntiPatterns: [
            'No caching',
            'Synchronous processing',
            'Poor database design',
            'No load balancing',
            'Resource contention'
        ]
    }
];
// Layered Architecture Patterns
export const layeredArchitectureLayers = [
    {
        id: 'presentation',
        name: 'Presentation Layer',
        description: 'User interface and user experience components',
        responsibilities: [
            'User interaction handling',
            'Data presentation',
            'Input validation',
            'Session management',
            'Navigation flow'
        ],
        dependencies: ['application'],
        technologies: ['React', 'Angular', 'Vue.js', 'Blazor', 'ASP.NET MVC'],
        azureServices: [
            'Azure Static Web Apps',
            'Azure App Service',
            'Azure CDN',
            'Azure Front Door',
            'Azure API Management'
        ],
        patterns: [
            'Model-View-Controller (MVC)',
            'Model-View-ViewModel (MVVM)',
            'Component-Based Architecture',
            'Single Page Application (SPA)',
            'Progressive Web App (PWA)'
        ]
    },
    {
        id: 'application',
        name: 'Application Layer',
        description: 'Business logic coordination and workflow orchestration',
        responsibilities: [
            'Use case orchestration',
            'Business workflow coordination',
            'Application services',
            'Security enforcement',
            'Transaction management'
        ],
        dependencies: ['domain', 'infrastructure'],
        technologies: ['.NET Core', 'Java Spring', 'Node.js', 'Python FastAPI', 'Go'],
        azureServices: [
            'Azure App Service',
            'Azure Functions',
            'Azure Container Apps',
            'Azure Kubernetes Service',
            'Azure Logic Apps'
        ],
        patterns: [
            'Application Services',
            'Command Query Responsibility Segregation (CQRS)',
            'Mediator Pattern',
            'Facade Pattern',
            'Orchestration vs Choreography'
        ]
    },
    {
        id: 'domain',
        name: 'Domain Layer',
        description: 'Core business logic and domain models',
        responsibilities: [
            'Business rule enforcement',
            'Domain model implementation',
            'Business logic encapsulation',
            'Domain events',
            'Invariant validation'
        ],
        dependencies: [],
        technologies: ['Domain Models', 'Value Objects', 'Aggregates', 'Domain Services', 'Specifications'],
        azureServices: [
            'Azure Functions (Domain Logic)',
            'Azure Service Bus (Domain Events)',
            'Azure Event Grid (Domain Events)',
            'Azure Cosmos DB (Event Store)',
            'Azure SQL Database'
        ],
        patterns: [
            'Domain-Driven Design (DDD)',
            'Aggregate Pattern',
            'Value Object Pattern',
            'Repository Pattern',
            'Specification Pattern'
        ]
    },
    {
        id: 'infrastructure',
        name: 'Infrastructure Layer',
        description: 'Data persistence and external system integration',
        responsibilities: [
            'Data access implementation',
            'External service integration',
            'Cross-cutting concerns',
            'Technical services',
            'Framework integration'
        ],
        dependencies: [],
        technologies: ['Entity Framework', 'Dapper', 'ADO.NET', 'HTTP Clients', 'Message Queues'],
        azureServices: [
            'Azure SQL Database',
            'Azure Cosmos DB',
            'Azure Storage',
            'Azure Service Bus',
            'Azure Key Vault'
        ],
        patterns: [
            'Repository Pattern',
            'Unit of Work Pattern',
            'Data Mapper Pattern',
            'Gateway Pattern',
            'Adapter Pattern'
        ]
    }
];
// Distributed System Patterns
export const distributedPatterns = [
    {
        id: 'api-gateway',
        name: 'API Gateway',
        type: 'communication',
        description: 'Single entry point for all client requests to microservices',
        implementation: 'Centralized gateway that routes requests, handles authentication, and provides cross-cutting concerns',
        azureServices: ['Azure API Management', 'Azure Application Gateway', 'Azure Front Door'],
        prerequisites: ['Microservices architecture', 'Service discovery'],
        complexity: 'medium'
    },
    {
        id: 'circuit-breaker',
        name: 'Circuit Breaker',
        type: 'resilience',
        description: 'Prevent cascading failures by monitoring service health',
        implementation: 'Monitor failure rates and open circuit when threshold is exceeded',
        azureServices: ['Azure Service Fabric', 'Azure Functions', 'Azure App Service'],
        prerequisites: ['Service monitoring', 'Health checks'],
        complexity: 'medium'
    },
    {
        id: 'event-sourcing',
        name: 'Event Sourcing',
        type: 'data',
        description: 'Store events instead of current state',
        implementation: 'Append-only event store with event replay capabilities',
        azureServices: ['Azure Event Hubs', 'Azure Cosmos DB', 'Azure SQL Database'],
        prerequisites: ['Event-driven architecture', 'CQRS'],
        complexity: 'high'
    },
    {
        id: 'saga',
        name: 'Saga Pattern',
        type: 'data',
        description: 'Manage distributed transactions across multiple services',
        implementation: 'Orchestration or Choreography-based coordination',
        azureServices: ['Azure Logic Apps', 'Azure Service Bus', 'Azure Functions'],
        prerequisites: ['Microservices', 'Compensating actions'],
        complexity: 'high'
    },
    {
        id: 'cqrs',
        name: 'Command Query Responsibility Segregation',
        type: 'data',
        description: 'Separate read and write operations',
        implementation: 'Different models for commands and queries',
        azureServices: ['Azure Cosmos DB', 'Azure SQL Database', 'Azure Search'],
        prerequisites: ['Event sourcing (optional)', 'Separate data stores'],
        complexity: 'medium'
    },
    {
        id: 'bulkhead',
        name: 'Bulkhead',
        type: 'resilience',
        description: 'Isolate resources to prevent system-wide failures',
        implementation: 'Resource isolation using separate pools',
        azureServices: ['Azure Container Apps', 'Azure Kubernetes Service', 'Azure App Service'],
        prerequisites: ['Resource monitoring', 'Service isolation'],
        complexity: 'medium'
    }
];
// Architecture Decision Records Template
export const sampleDesignDecisions = [
    {
        id: 'highjump-data-architecture',
        title: 'HighJump WMS Data Architecture Strategy',
        context: 'Legacy HighJump WMS system requires modernization with cloud-native architecture while maintaining operational continuity',
        problem: 'Current monolithic database architecture cannot scale with business growth and lacks modern observability',
        options: [
            {
                name: 'Lift and Shift to Azure SQL',
                pros: ['Minimal code changes', 'Quick migration', 'Familiar technology'],
                cons: ['Limited scalability', 'Expensive at scale', 'Monolithic limitations remain'],
                cost: 'medium',
                complexity: 'low'
            },
            {
                name: 'Microservices with Azure Cosmos DB',
                pros: ['Global scale', 'Multiple consistency models', 'Schema flexibility'],
                cons: ['High complexity', 'Learning curve', 'Data consistency challenges'],
                cost: 'high',
                complexity: 'high'
            },
            {
                name: 'Hybrid: Azure SQL + Cosmos DB',
                pros: ['Best of both worlds', 'Gradual migration', 'Technology fit per use case'],
                cons: ['Data synchronization', 'Multiple technologies to manage'],
                cost: 'medium',
                complexity: 'medium'
            }
        ],
        recommendation: 'Hybrid: Azure SQL + Cosmos DB',
        rationale: 'Provides optimal balance of performance, scalability, and migration risk while allowing gradual modernization',
        consequences: [
            'Need data synchronization strategy',
            'Team needs training on Cosmos DB',
            'Complex monitoring requirements'
        ],
        status: 'proposed',
        tags: ['data-architecture', 'highjump', 'migration', 'azure']
    },
    {
        id: 'microservices-communication',
        title: 'Inter-Service Communication Pattern',
        context: 'Decomposing HighJump monolith into microservices requires efficient communication strategy',
        problem: 'Need to balance consistency, performance, and resilience in service communication',
        options: [
            {
                name: 'Synchronous HTTP/REST',
                pros: ['Simple to implement', 'Easy debugging', 'Immediate consistency'],
                cons: ['Tight coupling', 'Cascade failures', 'Performance bottlenecks'],
                cost: 'low',
                complexity: 'low'
            },
            {
                name: 'Asynchronous Messaging',
                pros: ['Loose coupling', 'Better resilience', 'Natural scalability'],
                cons: ['Eventual consistency', 'Complex debugging', 'Message ordering'],
                cost: 'medium',
                complexity: 'medium'
            },
            {
                name: 'Hybrid: Sync + Async',
                pros: ['Best fit per use case', 'Optimal performance', 'Flexible consistency'],
                cons: ['Architecture complexity', 'Multiple communication patterns'],
                cost: 'medium',
                complexity: 'high'
            }
        ],
        recommendation: 'Hybrid: Sync + Async',
        rationale: 'Critical operations use sync for consistency, non-critical use async for performance and resilience',
        consequences: [
            'Need clear guidelines for pattern selection',
            'Requires robust monitoring and tracing',
            'Team training on both patterns'
        ],
        status: 'approved',
        tags: ['microservices', 'communication', 'architecture']
    }
];
export const designFrameworkData = {
    wellArchitectedPillars,
    layeredArchitectureLayers,
    distributedPatterns,
    sampleDesignDecisions
};
